import styled from "styled-components";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import { colorAttentionBlue10, colorAttentionBlue50, colorMiscBlue70, colorMiscBlue80, colorFunctionalGray10, colorFunctionalGray20, colorFunctionalGray30, colorMiscGray40, colorFunctionalGray50, colorFunctionalGray60, colorFunctionalGray70, fontSizeS, iconSizeM, colorFunctionalBlack, borderRadiusS, iconSizeL, iconSizeMl, spacingM, spacingS, spacingSm, spacingXs, spacingXxs, fontSizeM, } from "@rhumbix/rmbx_design_system_design_tokens";
import Checkbox from "../Checkbox";
const getShowActiveStyles = ({ selectProps, isMulti }) => selectProps.menuIsOpen && !isMulti;
/** Styles for React Select. See https://react-select.com/styles "the Styles prop" for an explanation and
 * a list of the supported inner components
 */
export const customSelectStyles = {
    control: (styles, props) => {
        const showActiveStyles = getShowActiveStyles(props);
        return {
            ...styles,
            display: "flex",
            backgroundColor: showActiveStyles ? colorAttentionBlue10 : "inherit",
            border: `1px ${showActiveStyles ? colorAttentionBlue10 : colorFunctionalGray50} solid`,
            boxShadow: "none",
            flexWrap: "wrap",
            alignItems: "center",
            // HACK: The selector input field needs to grow vertically to accommodate multiple chiclets,
            // so we can't use a fixed height with box-sizing: border-box.
            // Using an off-by-one padding value accounts for the 1px border and results in the desired height.
            padding: "7px",
            ":hover": {
                border: `1px ${showActiveStyles ? colorAttentionBlue10 : colorFunctionalGray50} solid`,
            },
        };
    },
    dropdownIndicator: (styles, props) => {
        const showActiveStyles = getShowActiveStyles(props);
        return { ...styles, padding: 0, color: showActiveStyles ? colorAttentionBlue50 : colorFunctionalGray70 };
    },
    indicatorsContainer: styles => ({ ...styles, fontSize: "12px", cursor: "pointer" }),
    indicatorSeparator: (styles, props) => {
        const showActiveStyles = getShowActiveStyles(props);
        return {
            ...styles,
            backgroundColor: showActiveStyles ? colorAttentionBlue50 : colorMiscGray40,
            margin: 0,
        };
    },
    input: styles => ({
        ...styles,
        color: colorMiscBlue80,
        fontSize: fontSizeS,
        lineHeight: "16px",
        margin: 0,
        padding: spacingS,
        "& input": {
            font: "inherit",
        },
    }),
    multiValue: (styles, { data, options }) => {
        const selectedOption = options?.filter(opt => opt.value === data.value)[0];
        // The options may not be loaded yet, and selectedOption may be null -
        // but if data, which represents a selection, has isFixed set to true, fall back
        // to that value in order to set the styles
        const isFixed = selectedOption ? selectedOption.isFixed : data.isFixed;
        return {
            ...styles,
            backgroundColor: isFixed ? colorFunctionalGray20 : colorAttentionBlue10,
            padding: spacingS,
            margin: 0,
        };
    },
    multiValueRemove: (styles, { data, options }) => {
        const selectedOption = options?.filter(opt => opt.value === data.value)[0];
        const isFixed = selectedOption ? selectedOption.isFixed : data.isFixed;
        return {
            ...styles,
            ...(isFixed
                ? {
                    display: "none",
                }
                : {
                    color: colorAttentionBlue50,
                    cursor: "pointer",
                }),
        };
    },
    multiValueLabel: (styles, { data, options }) => {
        const selectedOption = options?.filter(opt => opt.value === data.value)[0];
        const isFixed = selectedOption ? selectedOption.isFixed : data.isFixed;
        return {
            ...styles,
            borderRadius: borderRadiusS,
            color: isFixed ? colorFunctionalGray60 : colorMiscBlue70,
            fontSize: fontSizeS,
            fontWeight: 500,
            lineHeight: "16px",
            paddingLeft: "unset",
            padding: 0,
        };
    },
    option: (styles, { isFocused, isSelected }) => ({
        ...styles,
        backgroundColor: isSelected ? colorFunctionalGray10 : isFocused ? colorAttentionBlue10 : "unset",
        cursor: "pointer",
        ":active": {
            ...styles[":active"],
            backgroundColor: "unset",
        },
        display: "flex",
        alignItems: "center",
    }),
    placeholder: (styles, props) => {
        const showActiveStyles = getShowActiveStyles(props);
        const { showAllWhenNoneSelected } = props.selectProps;
        return {
            ...styles,
            fontSize: fontSizeS,
            lineHeight: fontSizeM,
            color: showAllWhenNoneSelected && !showActiveStyles ? colorFunctionalBlack : colorFunctionalGray50,
            margin: 0,
            padding: spacingS,
        };
    },
    singleValue: (styles, props) => {
        const showActiveStyles = getShowActiveStyles(props);
        return {
            ...styles,
            color: showActiveStyles ? colorMiscBlue70 : colorFunctionalBlack,
            fontSize: fontSizeS,
            fontWeight: 500,
            margin: 0,
            padding: spacingS,
        };
    },
    valueContainer: styles => ({ ...styles, padding: 0, gap: spacingS }),
};
/** Styles for the GroupedSelector */
export const groupedSelectorStyles = {
    group: (baseStyles, state) => ({
        ...baseStyles,
        paddingTop: 0,
        paddingBottom: 0,
    }),
    groupHeading: (baseStyles, state) => ({
        ...baseStyles,
        paddingLeft: 0,
        paddingRight: 0,
    }),
    menuList: (baseStyles, state) => ({
        ...baseStyles,
        paddingTop: 0,
    }),
    option: (baseStyles, state) => ({
        ...baseStyles,
        borderBottom: `1px solid ${colorFunctionalGray30}`,
        padding: `${spacingS} ${spacingM} ${spacingXs} ${spacingM}`,
        display: "flex",
        flexDirection: "row",
    }),
};
export const StyledClearIndicator = styled.div `
    font-weight: normal;
    font-size: ${fontSizeS};
    color: ${props => (getShowActiveStyles(props) ? colorMiscBlue80 : colorFunctionalGray60)};
    cursor: pointer;
    margin-right: ${spacingM};
    line-height: 0;

    svg {
        color: ${props => (getShowActiveStyles(props) ? colorAttentionBlue50 : colorMiscGray40)};
        width: ${iconSizeL};
    }
`;
export const StyledDropdownIndicator = styled.div `
    margin-left: ${spacingSm};
    margin-right: ${spacingS};
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
        color: ${props => (getShowActiveStyles(props) ? colorAttentionBlue50 : colorFunctionalGray70)};
        width: ${iconSizeMl};
    }
`;
export const StyledMultiValueRemove = styled.div `
    display: flex;
    align-items: center;
    justify-content: center;
    margin-left: ${spacingSm};
    padding-left: 0 !important;
    padding-right: 0 !important;

    :hover {
        background-color: inherit !important;
    }

    svg {
        color: ${colorMiscBlue70};
        width: 11px;
    }
`;
/** Multi-select Option Checkbox */
export const StyledCheckbox = styled(Checkbox) `
    width: ${iconSizeM};
    margin: auto 10px auto 1px !important;
`;
export const OptionWrapper = styled.span `
    display: flex;
    padding: 8px 16px 4px 16px;
    width: 100%;
`;
/** Default option */
export const StyledDefaultOption = styled.span `
    color: ${colorFunctionalGray70};
`;
/** React Select */
export const StyledSelect = styled(Select) `
    .select-option {
        font-size: ${fontSizeS};
        padding: 0;

        svg,
        input {
            cursor: pointer;
            margin-right: ${spacingS};
            vertical-align: middle;
        }

        svg.icon-check {
            color: ${colorAttentionBlue50};
            width: ${iconSizeM};
        }

        :last-child {
            border-bottom: none;
        }
    }
`;
/** React Creatable Select */
export const StyledCreatableSelect = styled(CreatableSelect) `
    .select-option {
        font-size: ${fontSizeS};
        padding: 0;

        svg,
        input {
            cursor: pointer;
            margin-right: ${spacingS};
            vertical-align: middle;
        }

        svg.icon-check {
            color: ${colorAttentionBlue50};
            width: ${iconSizeM};
        }

        :last-child {
            border-bottom: none;
        }
    }
`;
/** React Select Options */
export const StyledOption = styled.div `
    color: ${colorFunctionalGray70};
    .option-primarySubtitle {
        color: ${colorFunctionalGray50};
    }
    display: grid;
    grid-auto-flow: row;
    grid-row-gap: ${spacingXxs};
    div {
        margin-bottom: ${spacingXs};
    }
`;
export const StyledOptionPrefix = styled.div `
    font-size: ${spacingSm};
`;
export const StyledOptionTitle = styled.div `
    font-size: 14px;
    font-weight: 500;
    margin-right: ${spacingS};
`;
export const StyledOptionPrimarySubtitle = styled.div `
    color: ${colorFunctionalGray50};
    font-size: ${spacingSm};
`;
export const StyledOptionSecondarySubtitle = styled.div `
    font-size: ${spacingSm};
`;
